import { useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LenisContext } from "@/App";
import { MaskedLine, Reveal, ClipReveal, EASE } from "@/components/Motion";
import { products } from "@/data/products";

const Hero = () => {
  const lenisRef = useContext(LenisContext);

  return (
    <section data-testid="hero-section" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#262A18]">
      <div className="relative z-10 mx-auto max-w-4xl px-6 pb-28 pt-36 text-center">
        <div className="slow-drift mx-auto max-w-[240px] sm:max-w-[280px]">
          <ClipReveal delay={0.2} className="border border-[#EFE8D2]/25 p-2">
            <img
              data-testid="hero-logo-artwork"
              src="/logo.webp"
              alt="Faeloria hand-painted logo artwork"
              className="w-full object-cover"
            />
          </ClipReveal>
        </div>

        <h1 data-testid="hero-headline" className="mt-12 font-serif text-4xl leading-[1.1] text-[#EFE8D2] sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.4}>Providing Natural and Holistic Ways to Alchemize and Heal</MaskedLine>
        </h1>

        <Reveal delay={0.55}>
          <div className="mx-auto mt-10 h-px w-40 bg-[#B3A264]/50" aria-hidden="true" />
        </Reveal>

        <Reveal delay={0.7}>
          <p data-testid="hero-subline" className="mx-auto mt-10 max-w-2xl text-base leading-relaxed text-[#B9AE94] lg:text-lg">
            Faeloria Collective provides holistic, natural approaches to wellness
            through thoughtfully crafted herbal formulas. Rooted in traditional herbal
            knowledge, our goal is to make plant-based wellness simple and intentional.
          </p>
        </Reveal>

        <Reveal delay={0.85}>
          <div className="mt-12 flex flex-col items-center justify-center gap-5 sm:flex-row">
            <button
              data-testid="hero-explore-button"
              onClick={() => lenisRef.current?.scrollTo("#collection", { offset: -60 })}
              className="group inline-flex items-center gap-4 bg-[#EFE8D2] px-8 py-4 text-xs uppercase tracking-[0.25em] text-[#262A18] transition-colors duration-500 hover:bg-white"
            >
              Explore the creations
              <ArrowDown size={14} className="transition-transform duration-500 group-hover:translate-y-1" />
            </button>
            <Link
              data-testid="hero-index-link"
              to="/info"
              className="inline-flex items-center gap-3 border border-[#EFE8D2]/40 px-8 py-4 text-xs uppercase tracking-[0.25em] text-[#EFE8D2] transition-colors duration-500 hover:bg-[#EFE8D2]/10"
            >
              Herbal index
            </Link>
          </div>
        </Reveal>
      </div>

      <motion.div
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 1.4, delay: 1.4, ease: EASE }}
        className="absolute bottom-0 left-1/2 z-10 h-24 w-px origin-top bg-[#B3A264]/60"
      />
    </section>
  );
};

const MOODS = {
  "mental-fatigue-support": "Focus",
  "zen-mind-herbal-tea": "Calm",
  "smokers-blend-pre-roll": "Smoke",
  "yoni-steam": "Ritual",
  "gut-support-herbal-tea": "Digest",
  "raspberry-leaf-tea": "Nourish",
};

const Collection = () => (
  <section id="collection" data-testid="collection-section" className="mx-auto max-w-[1400px] px-6 py-28 lg:px-12 lg:py-36">
    <div className="mb-16 text-center lg:mb-20">
      <Reveal>
        <p className="mb-6 text-xs uppercase tracking-[0.4em] text-gilt">Creations</p>
      </Reveal>
      <h2 className="font-serif text-5xl leading-[1.02] text-ivory lg:text-7xl" data-testid="collection-heading">
        <MaskedLine inView>The shelf</MaskedLine>
      </h2>
    </div>

    <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2">
      {products.map((p, i) => (
        <Reveal key={p.slug} delay={0.05 * (i % 2)}>
          <Link
            to={`/collection/${p.slug}`}
            data-testid={`product-card-${p.slug}`}
            className="group block border border-moss-700 p-10 transition-colors duration-500 hover:bg-moss-900/60 lg:p-12"
          >
            <div className="flex items-baseline justify-between gap-4">
              <p className="text-xs uppercase tracking-[0.3em] text-gilt">{MOODS[p.slug]}</p>
              <span className="font-serif text-sm italic text-sand">{p.index}</span>
            </div>
            <h3 className="mt-10 font-serif text-3xl text-ivory transition-colors duration-500 group-hover:text-gilt lg:text-4xl">
              {p.name}
            </h3>
            <p className="mt-4 font-serif text-lg italic leading-snug text-sand">{p.tagline}</p>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  const { hash } = useLocation();
  const lenisRef = useContext(LenisContext);

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => lenisRef.current?.scrollTo(hash, { offset: -60 }), 150);
      return () => clearTimeout(t);
    }
  }, [hash, lenisRef]);

  return (
    <main data-testid="home-page">
      <Hero />
      <Collection />
    </main>
  );
}
